/**
 * The Connectors page's route contract — parsing and building, and nothing else.
 *
 * Every state of the page is a URL: which tab, and which entry is being pointed at. That is not
 * tidiness, it is the reason the page works at all — a push notification saying "Gmail's access
 * expired" has to land ON Gmail, and returning from a connector's OAuth redirect has to land back
 * where it started. State kept only in React would lose both.
 *
 * Deliberately pure and dependency-free: the native resolvers implement the same contract
 * (`DeepLink.swift`, `RouteResolver.kt`), and this is the shape they mirror. Keep the three in step
 * — see `docs/connectors-page-design.md` §1.
 */

export const CONNECTORS_BASE = '/settings/connectors';

/** Channels (the surfaces Symphony hears you through) · Tools · Outreach (the channels it reaches people
 *  on — the slug stays `channels-out`; the label is the map's word). The slugs are a deeplink contract the
 *  natives mirror. The DEFAULT tab is still Tools (below). */
export const CONNECTORS_TABS = ['channels', 'tools', 'channels-out'] as const;
export type ConnectorsTab = (typeof CONNECTORS_TABS)[number];

/**
 * Tools, not channels.
 *
 * The page is named for what most people came for, and the tab order follows: connecting a tool is
 * the common errand, while channels are set up once and then forgotten.
 */
export const DEFAULT_CONNECTORS_TAB: ConnectorsTab = 'tools';

/**
 * The channel groups. One diagram node, one section, one slug — the sets are identical, which is the
 * invariant `heroChannelNodes` spells out: a section the diagram never mentions is a section nothing
 * points at. So a group added here gets a node AND a section, or it does not get a slug.
 *
 * `meetings` is her joining the user's own video calls — a channel because it is a way she reaches
 * them, next to WhatsApp and the apps, and because the connect metaphor IS the consent: a row means
 * she may join, no row means never. Both native resolvers carry it too, so a `channels/meetings`
 * deeplink lands on the same section on web, iOS and Android.
 *
 * The slug is listed here UNCONDITIONALLY even though the section is behind `symphonyMeetingsAgent`,
 * because this is the route CONTRACT — the set of slugs that parse — and it is mirrored 1:1 by the
 * native resolvers, which conduct nothing. What the rollout gates is whether anything renders for it:
 * `ChannelsBody` and `mobile/ChannelsMobile` each drop it from their own section list, so outside the
 * release a `channels/meetings` link parses and lands on a tab with no such section. That is the same
 * place an unknown slug lands, and it is why the gate needs no branch here.
 *
 * ⚠️ The rendered hero (`ConnectorsGenome`) is NOT gated with them, and its channel count is the
 * hand-maintained `GENOME_SUPPORTED_CHANNELS` rather than this list — so in the release its overflow
 * chip is one short. Left alone deliberately: that constant is outside this change, and gating it is
 * its own decision. `heroChannelNodes` is not the diagram either — it feeds `ConnectorsHero`, which
 * nothing renders.
 *
 * `wearables` is the hands-free group — the ways to reach the agent without opening anything: Siri, and
 * so the earbuds it already speaks through today; a watch it does not run on yet. It is deliberately
 * about REACHING the agent, not about a device's data. A wearable whose recordings the agent READS is an
 * ordinary connector and lives on the Tools tab, which is where this section sends you for it — the two
 * halves of "do you work with my wearable?" have different answers and must not be collapsed into one row.
 */
export const CHANNEL_SLUGS = ['symphony-apps', 'whatsapp', 'meetings', 'ai-coders', 'gemini-enterprise', 'wearables'] as const;
export type ChannelSlug = (typeof CHANNEL_SLUGS)[number];

export interface ConnectorsRoute {
  tab: ConnectorsTab;
  /** The entry to scroll to and highlight. Absent means the top of the tab. */
  target?: string;
  /** A WhatsApp group's own screen — the one sub-route that is a screen, not a scroll position. */
  whatsappGroupId?: string;
}

function isTab(value: string | undefined): value is ConnectorsTab {
  return !!value && (CONNECTORS_TABS as readonly string[]).includes(value);
}

/**
 * Parse the path segments after the base into a route.
 *
 * **Never rejects.** A stale or malformed link — an old push, a truncated share, a connector slug
 * that has since been renamed — must land somewhere sensible rather than 404. The worst outcome
 * here is the default tab, which is exactly what a bare visit gets.
 */
export function parseConnectorsRoute(segments: readonly string[]): ConnectorsRoute {
  const [rawTab, target, ...rest] = segments.filter(Boolean).map((s) => s.toLowerCase());

  if (!isTab(rawTab)) {
    // No tab, or something unrecognised where the tab should be. If the first segment looks like a
    // channel group, treat it as a channels target — that rescues a link written without the tab.
    if (rawTab && (CHANNEL_SLUGS as readonly string[]).includes(rawTab)) {
      return { tab: 'channels', target: rawTab };
    }
    return { tab: DEFAULT_CONNECTORS_TAB };
  }

  if (!target) return { tab: rawTab };

  // `channels/whatsapp/group/{id}` — the group's own screen.
  if (rawTab === 'channels' && target === 'whatsapp' && rest[0] === 'group' && rest[1]) {
    return { tab: 'channels', target: 'whatsapp', whatsappGroupId: rest[1] };
  }

  // A `group` segment with no id is a malformed group link (a truncated push, an id that failed to
  // interpolate). Land on the WhatsApp section rather than nowhere — mirrors the native resolvers,
  // which fall back to the hub for exactly this case.
  return { tab: rawTab, target };
}

/** Build the canonical path for a route. The inverse of {@link parseConnectorsRoute}. */
export function connectorsPath(route?: Partial<ConnectorsRoute>): string {
  const tab = route?.tab ?? DEFAULT_CONNECTORS_TAB;
  if (route?.whatsappGroupId) {
    return `${CONNECTORS_BASE}/channels/whatsapp/group/${route.whatsappGroupId}`;
  }
  if (route?.target) return `${CONNECTORS_BASE}/${tab}/${route.target}`;
  return `${CONNECTORS_BASE}/${tab}`;
}

/**
 * The DOM id of the element a target scrolls to.
 *
 * Shared by the builder and the page so a route can never point at an id the page doesn't render —
 * the failure mode is silent (nothing scrolls, nothing highlights, the click looks broken).
 */
export function connectorsTargetElementId(tab: ConnectorsTab, target: string): string {
  return `row-${tab}-${target}`;
}
